import { clamp } from "es-toolkit";
import { useEffect, useRef } from "react";
import DefaultNoBodyBackgroundColor from "./NoBodyBackgroundColor";
import DefaultVideo from "./video/Video";
import { Display } from "src/pods/ideas";
import DefaultPairShareResult from "src/pods/pairshare/components/receiver/PairShareResult";
import DefaultPresentationPage from "src/shared/components/PresentationPage";

type IdeasDisplayProps = {
  displayClose?: VoidFunction;
  sendVideoPlaybackState?: (value: boolean) => void;
  display?: Display;
  /**
   * DI for storybook stories — production callers leave undefined. Stubs:
   *   - `Video`: avoids the RadiantMP browser dependency.
   *   - `PresentationPage` / `PairShareResult` / `NoBodyBackgroundColor`:
   *     sidesteps the receiver pairshare pipeline + document-body mutation.
   */
  di?: {
    Video?: React.ComponentType<React.ComponentProps<typeof DefaultVideo>>;
    PairShareResult?: React.ComponentType<React.ComponentProps<typeof DefaultPairShareResult>>;
    PresentationPage?: React.ComponentType<React.ComponentProps<typeof DefaultPresentationPage>>;
    NoBodyBackgroundColor?: React.ComponentType<React.ComponentProps<typeof DefaultNoBodyBackgroundColor>>;
  };
};

/**
 * IdeasDisplay is the receiver-side screen rendered at `/apps/display/ideas`
 * during a paired casting session. Two variants:
 *   - `display.question` set: cast pairshare question on the display.
 *   - `display.watch` set: cast HLS video full-screen.
 *
 * Converted from class → function. The seek-on-seekOffset-change effect
 * keeps the original imperative `Video.seek(...)` semantics; the play/pause
 * forwarders are unchanged.
 */
export default function IdeasDisplay({ display, sendVideoPlaybackState, displayClose, di }: IdeasDisplayProps) {
  const Video = di?.Video ?? DefaultVideo;
  const PairShareResult = di?.PairShareResult ?? DefaultPairShareResult;
  const PresentationPage = di?.PresentationPage ?? DefaultPresentationPage;
  const NoBodyBackgroundColor = di?.NoBodyBackgroundColor ?? DefaultNoBodyBackgroundColor;

  // Imperative subset we call on the Video instance. The real `DefaultVideo`
  // class exposes these; story stubs are function components and won't
  // populate the ref (the seek effect guards on `videoRef.current`).
  const videoRef = useRef<{
    seek: (to: number) => void;
    getCurrentTime: () => number;
    getDuration: () => number;
  } | null>(null);
  const previousSeekOffsetRef = useRef<number | undefined>(display?.seekOffset);

  // Imperatively seek the Video when seekOffset changes — preserves the
  // pre-migration `UNSAFE_componentWillReceiveProps` semantics. Both offsets
  // must be defined and the delta non-zero (otherwise it's a no-op or a
  // first-render).
  useEffect(() => {
    const previous = previousSeekOffsetRef.current;
    const next = display?.seekOffset;
    if (next && previous) {
      const timeSeeked = next - previous;
      const video = videoRef.current;
      if (video && timeSeeked !== 0) {
        video.seek(clamp(video.getDuration(), 0, video.getCurrentTime() + timeSeeked * 1000));
      }
    }
    previousSeekOffsetRef.current = next;
  }, [display?.seekOffset]);

  const handleVideoPlaying = () => {
    if (!display?.isPlaying) {
      sendVideoPlaybackState?.(true);
    }
  };

  const handleVideoPause = () => {
    if (display?.isPlaying) {
      sendVideoPlaybackState?.(false);
    }
  };

  const handleVideoEnd = () => sendVideoPlaybackState?.(false);

  if (!display) {
    return null;
  }

  if (display.question) {
    return (
      <PresentationPage variant="ideasQuestion" onRequestHide={() => displayClose?.()}>
        <PairShareResult question={display.question} />
      </PresentationPage>
    );
  }

  if (display.watch) {
    // `Video` here may be the real class (production) or a story stub
    // (function). The `ref` only fires for the class instance; the stub
    // path leaves `videoRef.current` null, which the seek effect tolerates.
    // Cast via React.ElementType to permit ref on the conditional component.
    // (Tightening to `React.ElementType<React.ComponentProps<typeof DefaultVideo>>`
    // produces TS error 2590 "union type too complex to represent" — keep broad.)
    const VideoEl = Video as React.ElementType;
    return (
      <PresentationPage
        variant="ideasVideo"
        onRequestHide={() => {
          sendVideoPlaybackState?.(false);
          displayClose?.();
        }}
      >
        <div>
          <NoBodyBackgroundColor />
          <VideoEl
            ref={(video: typeof videoRef.current) => {
              videoRef.current = video;
            }}
            type={display.watch.type}
            watch={display.watch}
            autoplay={false}
            isExpanded={true}
            isFullscreen={display.isFullscreen}
            isPlaying={display.isPlaying}
            onPlaying={handleVideoPlaying}
            onPause={handleVideoPause}
            onEnd={handleVideoEnd}
          />
        </div>
      </PresentationPage>
    );
  }

  return null;
}
